import React, { useRef, useState, useEffect } from "react";
import { Button, ProgressBar } from "react-bootstrap";
import { collection, documentId, getDocs, limit, orderBy, query, runTransaction, startAfter } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import { db, functions } from "../../../firebase";
import { notifyError, notifyTopRight, notifyWarning } from "../toast/SetToast";
import { ahiErrorLogCsv, isEmptyBookingAhi, runBookingAhiRefill, updateEmptyBookingAhi } from "./BookingAhiRefillUtils";
import { readBookingReportAhi, withAhiTimeout } from "./BookingAhiReportReader";

export const canRefillBookingAhi = (user) => Boolean(user && Array.isArray(user.roles) && user.roles.includes("super_admin"));
const ALL_NODES = "__all_accessible_nodes__";

export default function BookingAhiRefill({ currentUser, nodes = [], filterAccessibleDocs, onSaved, onBusyChange, disabled }) {
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [selectedNode, setSelectedNode] = useState("");
  const running = useRef(false);
  const mounted = useRef(true);
  const activeUser = useRef(currentUser);
  activeUser.current = currentUser;
  const availableNodes = nodes.filter((node) => node && node.id);
  const activeScope = useRef(null);
  activeScope.current = { nodes: availableNodes, disabled };
  useEffect(() => () => { mounted.current = false; }, []);
  const exportLog = (result) => saveAs(new Blob([ahiErrorLogCsv(result.errors)], { type: "text/csv;charset=utf-8" }), `booking-ahi-errors-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`);

  const start = async () => {
    if (running.current || disabled || !canRefillBookingAhi(currentUser)) return;
    const selectedNodes = selectedNode === ALL_NODES ? availableNodes : availableNodes.filter((node) => node.id === selectedNode);
    if (!selectedNodes.length) return;
    const nodeIds = Array.from(new Set(selectedNodes.map((node) => node.id)));
    const scope = selectedNode === ALL_NODES ? `All accessible nodes (${nodeIds.length})` : selectedNodes[0].nodename || selectedNodes[0].id;
    running.current = true;
    setConfirming(true);
    let confirmed = false;
    try {
      confirmed = await swal({
        title: "Confirm AHI refill?",
        text: `Scope: ${scope}\n\nThis will scan every booking page in the selected scope and read Sleep Study Reports for cases with missing AHI. Table search and status filters do not limit this scan.\n\nThis may fetch a large amount of data and take time. Only empty AHI fields will be filled. Continue?`,
        icon: "warning",
        buttons: ["Cancel", "Confirm refill"],
      });
    } catch (error) {
      notifyError("Unable to open the refill confirmation. Please try again.");
    } finally {
      if (mounted.current) setConfirming(false);
    }
    if (!confirmed || !mounted.current || activeScope.current.disabled || !canRefillBookingAhi(activeUser.current) || activeUser.current.uid !== currentUser.uid || nodeIds.some((id) => !activeScope.current.nodes.some((node) => node.id === id))) {
      running.current = false;
      return;
    }
    if (onBusyChange) onBusyChange(true);
    setBusy(true);
    let result = { total: 0, processed: 0, updated: 0, skipped: 0, failed: 0, errors: [], scanned: 0, phase: "scanning", scope };
    setProgress(result);
    const assertActive = () => {
      if (!mounted.current || !canRefillBookingAhi(activeUser.current) || activeUser.current.uid !== currentUser.uid) {
        throw new Error("Refill stopped because the page or signed-in account changed.");
      }
    };
    try {
      const cases = [];
      for (const nodeId of nodeIds) {
        let cursor;
        while (true) {
          assertActive();
          const constraints = [orderBy(documentId()), limit(250)];
          if (cursor) constraints.push(startAfter(cursor));
          const snapshot = await withAhiTimeout(getDocs(query(collection(db, "nodes", nodeId, "bookings"), ...constraints)), "Booking scan");
          filterAccessibleDocs(snapshot.docs).forEach((row) => {
            const data = row.data();
            const props = data.extendedProps || {};
            if (String(props.hn || "").trim() && isEmptyBookingAhi(props.ahi)) cases.push({ ref: row.ref, data });
          });
          result = { ...result, scanned: result.scanned + snapshot.docs.length, total: cases.length };
          if (mounted.current) setProgress(result);
          if (snapshot.docs.length < 250) break;
          cursor = snapshot.docs[snapshot.docs.length - 1];
        }
      }
      const getSignedUrl = httpsCallable(functions, "getSignedUrl");
      result = await runBookingAhiRefill({
        cases,
        beforeCase: assertActive,
        readAhi: async (item) => {
          assertActive();
          const hn = String((item.data.extendedProps || {}).hn || "").trim();
          if (!hn) throw new Error("Booking has no HN.");
          const snapshot = await withAhiTimeout(getDocs(collection(db, "patients", hn, "report")), "Patient reports query");
          return readBookingReportAhi({ item, reports: snapshot.docs.map((row) => ({ ...row.data(), id: row.id })), getSignedUrl });
        },
        saveAhi: (item, ahi) => {
          assertActive();
          return updateEmptyBookingAhi({ db, runTransaction, bookingRef: item.ref, original: item.data.extendedProps || {}, ahi });
        },
        onSaved: (item, ahi, saved) => { if (mounted.current) onSaved(item, ahi, saved); },
        onProgress: (next) => {
          result = { ...next, phase: "processing", scope };
          if (mounted.current) setProgress(result);
        },
      });
      result = { ...result, phase: "done", scope };
      const message = `AHI refill complete (${scope}): ${result.updated} refilled, ${result.failed} failed, ${result.skipped} skipped / ${result.total} cases`;
      if (result.failed || result.skipped) notifyWarning(message, { autoClose: 10000 });
      else notifyTopRight(message, { autoClose: 10000 });
    } catch (error) {
      result = { ...result, phase: "stopped", errors: [...result.errors, { stage: result.phase, error: error.message || String(error) }] };
      notifyError(`AHI refill stopped: ${error.message || error}`);
    } finally {
      if (mounted.current) { setProgress(result); setBusy(false); }
      running.current = false;
      if (onBusyChange) onBusyChange(false);
      try { exportLog(result); }
      catch (error) { notifyError("Error-log download failed. Use Download error log to retry."); }
    }
  };

  if (!canRefillBookingAhi(currentUser)) return null;
  const percent = progress && progress.phase !== "scanning"
    ? (progress.total ? Math.floor(progress.processed / progress.total * 100) : progress.phase === "done" ? 100 : 0) : 0;
  return <div className="mb-3">
    <div className="d-flex flex-wrap align-items-end">
      <div className="mr-2 mb-2" style={{ width: 260, maxWidth: "100%" }}>
        <label htmlFor="booking-ahi-refill-node" className="small mb-1">Refill node</label>
        <select id="booking-ahi-refill-node" className="form-control form-control-sm" value={selectedNode} disabled={busy || confirming || disabled} onChange={(event) => setSelectedNode(event.target.value)}>
          <option value="">Select node…</option>
          <option value={ALL_NODES}>All accessible nodes ({availableNodes.length})</option>
          {availableNodes.map((node) => <option key={node.id} value={node.id}>{node.nodename || node.id}</option>)}
        </select>
      </div>
      <Button size="sm" className="mb-2" disabled={busy || confirming || disabled || !availableNodes.length || !(selectedNode === ALL_NODES || availableNodes.some((node) => node.id === selectedNode))} onClick={start} title="Confirm before scanning all booking pages in the selected node scope.">
        {busy ? "Refilling AHI…" : "Refill missing AHI from reports"}
      </Button>
    </div>
    <div className="small text-muted">Scans all pages in the selected node scope, regardless of table filters.</div>
    {progress && <div className="mt-2" role="status" aria-live="polite">
      <div className="small text-muted">{progress.scope}</div>
      {progress.phase === "scanning" ? `Scanning: ${progress.scanned} bookings checked · ${progress.total} missing AHI` :
        `${percent}% · ${progress.processed}/${progress.total} processed · ${progress.updated} refilled · ${progress.failed} failed · ${progress.skipped} skipped${progress.phase === "stopped" ? " · Stopped" : ""}`}
      <ProgressBar now={percent} label={`${percent}%`} animated={busy} className="mt-1" />
      {busy && <div className="small text-muted">Keep this page open until processing finishes.</div>}
      {!busy && <Button variant="link" size="sm" className="p-0 mt-1" onClick={() => exportLog(progress)}>Download error log ({progress.errors.length})</Button>}
    </div>}
  </div>;
}
